/*
 * DD Automaten-Standorte — store locator.
 *
 * Porta su Liquid la pagina React `dd-repo/src/pages/AutomatenStandorte.tsx`.
 * I dati NON stanno nel tema: la tabella `public.automaten` su Supabase e il
 * sistema di record (CLAUDE.md, `products`/ERP), il tema la legge in sola
 * lettura con la publishable key. Duplicare gli indirizzi nei blocchi della
 * sezione voleva dire tenerli allineati a mano, ed e esattamente il motivo per
 * cui la vecchia pagina mostrava "Cano — Singen" mentre il database aveva
 * "Neu Ulm Glacis Galerie".
 *
 * Regola sugli indirizzi: un automat senza indirizzo compilato non finisce
 * sulla mappa e non riceve un pulsante Route. Le righe incomplete hanno
 * lat/lng segnaposto (48/10, in mezzo alla campagna) e piazzarle come pin
 * significa mandare un cliente nel posto sbagliato. Restano in elenco con
 * l'etichetta "in preparazione". Stessa logica del JSON-LD in dd-standorte.liquid.
 */
(function () {
  'use strict';

  var WEEKDAY_SHORT = {
    'Montag': 'Mo', 'Dienstag': 'Di', 'Mittwoch': 'Mi', 'Donnerstag': 'Do',
    'Freitag': 'Fr', 'Samstag': 'Sa', 'Sonntag': 'So'
  };
  var GERMANY = [51.1657, 10.4515];
  var SCHEMA_DAY = {
    'Montag': 'https://schema.org/Monday', 'Dienstag': 'https://schema.org/Tuesday',
    'Mittwoch': 'https://schema.org/Wednesday', 'Donnerstag': 'https://schema.org/Thursday',
    'Freitag': 'https://schema.org/Friday', 'Samstag': 'https://schema.org/Saturday',
    'Sonntag': 'https://schema.org/Sunday'
  };

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.pow(Math.sin(dLat / 2), 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.pow(Math.sin(dLng / 2), 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function is24h(hours) {
    if (!Array.isArray(hours) || hours.length === 0) return false;
    return hours.every(function (d) {
      return d && !d.closed && Array.isArray(d.slots) && d.slots.length === 1 &&
        d.slots[0].open === '00:00' && d.slots[0].close === '24:00';
    });
  }

  function slotsKey(day) {
    if (!day || day.closed || !Array.isArray(day.slots) || day.slots.length === 0) return 'CLOSED';
    return day.slots.map(function (s) { return s.open + '-' + s.close; }).join('/');
  }

  /* Raggruppa i giorni contigui con lo stesso orario: "Mo–Sa 09:30–20:00"
     invece di sette righe identiche. Porting 1:1 di formatOpeningHours(). */
  function formatOpeningHours(hours, labels) {
    if (!Array.isArray(hours) || hours.length === 0) {
      return [{ label: '', value: labels.noData, isToday: false, isClosed: false }];
    }
    if (is24h(hours)) {
      return [{ label: '', value: labels.always, isToday: false, isClosed: false }];
    }

    var todayIndex = (new Date().getDay() + 6) % 7;
    var groups = [];
    for (var i = 0; i < hours.length; i++) {
      var key = slotsKey(hours[i]);
      var last = groups[groups.length - 1];
      if (last && last.key === key && last.endIdx === i - 1) {
        last.endIdx = i;
      } else {
        groups.push({ startIdx: i, endIdx: i, key: key });
      }
    }

    return groups.map(function (g) {
      var startShort = WEEKDAY_SHORT[hours[g.startIdx].day] || '';
      var endShort = WEEKDAY_SHORT[hours[g.endIdx].day] || '';
      var isClosed = g.key === 'CLOSED';
      return {
        label: g.startIdx === g.endIdx ? startShort : startShort + '–' + endShort,
        value: isClosed ? labels.closed : hours[g.startIdx].slots.map(function (s) {
          return s.open + '–' + s.close;
        }).join(' / '),
        isToday: todayIndex >= g.startIdx && todayIndex <= g.endIdx,
        isClosed: isClosed
      };
    });
  }

  function isFutureKickoff(iso) {
    if (!iso) return false;
    var d = new Date(iso);
    if (isNaN(d.getTime())) return false;
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    return d.getTime() > today.getTime();
  }

  function formatKickoff(iso, fallback) {
    if (!iso) return fallback;
    var d = new Date(iso);
    if (isNaN(d.getTime())) return fallback;
    var dateStr = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return 'Ab ' + dateStr + (isFutureKickoff(iso) ? '!' : '');
  }

  function mapRow(row) {
    var lat = parseFloat(row.lat);
    var lng = parseFloat(row.lng);
    var address = (row.address || '').trim();
    return {
      id: row.id,
      name: row.name || '',
      address: address,
      lat: lat,
      lng: lng,
      status: row.status === 'wartung' ? 'wartung' : 'online',
      products: row.products || '',
      openingHours: Array.isArray(row.opening_hours) ? row.opening_hours : [],
      kickoffDate: row.kickoff_date || null,
      /* vedi nota in testa al file */
      located: address !== '' && isFinite(lat) && isFinite(lng)
    };
  }

  function sortByNumber(a, b) {
    var na = parseInt((a.name.match(/#(\d+)/) || [0, '9999'])[1], 10);
    var nb = parseInt((b.name.match(/#(\d+)/) || [0, '9999'])[1], 10);
    return na - nb;
  }

  class DDStandorteElement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.labels = {
        closed: this.dataset.labelClosed || 'Geschlossen',
        always: this.dataset.labelAlways || 'Durchgehend nach Standortfreigabe',
        noData: this.dataset.labelNoData || 'Keine Daten',
        soon: this.dataset.labelSoon || 'Standort in Vorbereitung',
        route: this.dataset.labelRoute || 'Route',
        comingSoon: this.dataset.labelComingSoon || 'Coming Soon',
        online: this.dataset.labelOnline || 'Online',
        maintenance: this.dataset.labelMaintenance || 'Wartung',
        away: this.dataset.labelAway || 'km entfernt',
        count: this.dataset.labelCount || 'Standorte',
        loading: this.dataset.labelLoading || 'Standorte werden geladen…',
        error: this.dataset.labelError || 'Standorte konnten nicht geladen werden.',
        notFound: this.dataset.labelNotFound || 'Adresse nicht gefunden.',
        geoUnsupported: this.dataset.labelGeoUnsupported || 'Geolokalisierung wird nicht unterstützt.',
        geoFailed: this.dataset.labelGeoFailed || 'Standort konnte nicht ermittelt werden.',
        yourPosition: this.dataset.labelYourPosition || 'Dein Standort',
        reset: this.dataset.labelReset || 'Zurücksetzen',
        nearby: this.dataset.labelNearby || 'Automaten in deiner Nähe',
        fallback: this.dataset.labelFallback || 'Kein Automat im Umkreis — nächstgelegene Standorte angezeigt',
        showOnMap: this.dataset.labelShowOnMap || 'Auf der Karte'
      };

      this.radius = parseInt(this.dataset.radius || '100', 10);
      this.markerUrl = this.dataset.marker || '';
      this.list = this.querySelector('[data-dd-list]');
      this.countEl = this.querySelector('[data-dd-count]');
      this.statusEl = this.querySelector('[data-dd-status]');
      this.statusWrap = this.querySelector('[data-dd-status-wrap]');
      this.mapEl = this.querySelector('[data-dd-map]');
      this.input = this.querySelector('[data-dd-input]');
      this.markers = {};
      this.automaten = [];
      this.userPos = null;

      this.bindControls();
      this.fetchData();
    }

    bindControls() {
      var self = this;
      var searchBtn = this.querySelector('[data-dd-search]');
      var geoBtn = this.querySelector('[data-dd-geo]');
      var resetBtn = this.querySelector('[data-dd-reset]');
      var clearBtn = this.querySelector('[data-dd-clear]');

      if (searchBtn) searchBtn.addEventListener('click', function () { self.search(); });
      if (geoBtn) geoBtn.addEventListener('click', function () { self.geolocate(); });
      if (resetBtn) resetBtn.addEventListener('click', function () { self.reset(); });
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          self.input.value = '';
          self.input.focus();
          clearBtn.hidden = true;
        });
      }
      if (this.input) {
        this.input.addEventListener('input', function () {
          if (clearBtn) clearBtn.hidden = self.input.value.trim() === '';
        });
        this.input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); self.search(); }
        });
      }

      this.addEventListener('click', function (e) {
        var focusBtn = e.target.closest('[data-dd-focus]');
        if (focusBtn) self.focusMarker(focusBtn.getAttribute('data-dd-focus'));

        var thumb = e.target.closest('[data-dd-lightbox-open]');
        if (thumb) self.openLightbox(thumb.getAttribute('data-dd-lightbox-open'), thumb.getAttribute('data-dd-lightbox-alt'));

        if (e.target.closest('[data-dd-lightbox-close]')) self.closeLightbox();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeLightbox();
      });
    }

    setStatus(message, tone) {
      if (!this.statusEl || !this.statusWrap) return;
      this.statusEl.textContent = message || '';
      this.statusWrap.hidden = !message && !this.userPos;
      this.statusWrap.dataset.tone = tone || 'info';
    }

    fetchData() {
      var self = this;
      var endpoint = this.dataset.endpoint;
      var key = this.dataset.key;
      if (!endpoint || !key) {
        this.setStatus(this.labels.error, 'error');
        return;
      }

      this.setStatus(this.labels.loading, 'info');
      fetch(endpoint + '?select=*', { headers: { apikey: key, Authorization: 'Bearer ' + key } })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (rows) {
          self.automaten = (Array.isArray(rows) ? rows : []).map(mapRow).sort(sortByNumber);
          self.setStatus('', 'info');
          self.initMap();
          self.render();
          self.injectJsonLd();
        })
        .catch(function (err) {
          console.error('[dd-standorte]', err);
          self.setStatus(self.labels.error, 'error');
        });
    }

    logoIcon(online) {
      return L.divIcon({
        className: 'dd-standorte__marker' + (online ? '' : ' is-offline'),
        html: '<img src="' + esc(this.markerUrl) + '" alt="" width="46" height="46">',
        iconSize: [46, 46],
        iconAnchor: [23, 44],
        popupAnchor: [0, -42]
      });
    }

    userIcon() {
      var svg = '<span class="dd-standorte__user-pin"></span>';
      return L.divIcon({ className: 'dd-standorte__user-marker', html: svg, iconSize: [18, 18], iconAnchor: [9, 9] });
    }

    initMap() {
      if (this.map || typeof L === 'undefined' || !this.mapEl) return;

      this.map = L.map(this.mapEl, { attributionControl: true, scrollWheelZoom: false }).setView(GERMANY, 5);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap'
      }).addTo(this.map);

      /* scrollWheelZoom off di default: su mobile la mappa a tutta larghezza
         rubava lo scroll della pagina. Si riattiva al click sulla mappa. */
      var self = this;
      this.map.on('click', function () { self.map.scrollWheelZoom.enable(); });
      this.map.on('mouseout', function () { self.map.scrollWheelZoom.disable(); });
    }

    withDistance() {
      var self = this;
      return this.automaten.map(function (a) {
        var copy = Object.assign({}, a);
        copy.distance = (self.userPos && a.located)
          ? haversineKm(self.userPos[0], self.userPos[1], a.lat, a.lng)
          : null;
        return copy;
      });
    }

    visible() {
      var all = this.withDistance();
      if (!this.userPos) return all.sort(sortByNumber);

      var within = all.filter(function (a) { return a.distance !== null && a.distance <= this.radius; }, this);
      /* Mai "nessun risultato": se il raggio e vuoto si mostrano comunque i tre
         piu vicini. Un locator che risponde "niente" fa chiudere la pagina. */
      this.usingFallback = within.length === 0 && all.some(function (a) { return a.distance !== null; });
      if (this.usingFallback) {
        within = all.filter(function (a) { return a.distance !== null; })
          .sort(function (a, b) { return a.distance - b.distance; })
          .slice(0, 3);
      }
      return within.sort(sortByNumber);
    }

    render() {
      var items = this.visible();
      this.renderList(items);
      this.renderMarkers(items);
      this.fitMap(items);

      if (this.countEl) this.countEl.textContent = items.length + ' ' + this.labels.count;

      var resetBtn = this.querySelector('[data-dd-reset]');
      if (resetBtn) resetBtn.hidden = !this.userPos;

      if (this.userPos) {
        this.setStatus(
          this.usingFallback ? this.labels.fallback : items.length + ' ' + this.labels.nearby + ' (max. ' + this.radius + ' km)',
          'info'
        );
      }
    }

    renderList(items) {
      if (!this.list) return;
      var self = this;
      this.list.innerHTML = items.map(function (a) { return self.cardHtml(a); }).join('');
    }

    cardHtml(a) {
      var future = isFutureKickoff(a.kickoffDate);
      var hours = formatOpeningHours(a.openingHours, this.labels);
      var hoursHtml = hours.map(function (row) {
        return '<div class="dd-standorte__hours-row' + (row.isToday ? ' is-today' : '') + '">' +
          (row.label ? '<span class="dd-standorte__hours-day">' + esc(row.label) + '</span>' : '') +
          '<span' + (row.isClosed ? ' class="dd-standorte__hours-closed"' : '') + '>' + esc(row.value) + '</span>' +
          '</div>';
      }).join('');

      var statusHtml = future
        ? '<span class="dd-standorte__kickoff">' + esc(formatKickoff(a.kickoffDate, this.labels.maintenance)) + '</span>' +
          '<span class="dd-standorte__badge">' + esc(this.labels.comingSoon) + '</span>'
        : '<span class="dd-standorte__state' + (a.status === 'online' ? ' is-online' : '') + '">' +
          '<span class="dd-standorte__dot"></span>' +
          esc(a.status === 'online' ? this.labels.online : this.labels.maintenance) + '</span>';

      var addressHtml = a.located
        ? '<p class="dd-standorte__address">' + esc(a.address) + '</p>' +
          (a.distance !== null ? '<p class="dd-standorte__distance">' + a.distance.toFixed(1) + ' ' + esc(this.labels.away) + '</p>' : '')
        : '<p class="dd-standorte__soon">' + esc(this.labels.soon) + '</p>';

      var actionsHtml = a.located
        ? '<div class="dd-standorte__actions">' +
            '<button type="button" class="dd-standorte__ghost" data-dd-focus="' + esc(a.id) + '">' + esc(this.labels.showOnMap) + '</button>' +
            '<a class="dd-standorte__route" href="https://www.google.com/maps/dir/?api=1&amp;destination=' + a.lat + ',' + a.lng + '" target="_blank" rel="noopener noreferrer">' + esc(this.labels.route) + '</a>' +
          '</div>'
        : '';

      return '' +
        '<article class="dd-standorte__card' + (future ? ' is-upcoming' : '') + '">' +
          '<header class="dd-standorte__card-head">' +
            '<h3>' + esc(a.name) + '</h3>' +
            '<span class="dd-standorte__card-state">' + statusHtml + '</span>' +
          '</header>' +
          '<div class="dd-standorte__card-body">' +
            '<div class="dd-standorte__row">' +
              '<div class="dd-standorte__row-main">' + addressHtml + '</div>' +
              actionsHtml +
            '</div>' +
            '<div class="dd-standorte__hours">' + hoursHtml + '</div>' +
            (a.products ? '<p class="dd-standorte__products">' + esc(a.products) + '</p>' : '') +
          '</div>' +
        '</article>';
    }

    renderMarkers(items) {
      if (!this.map) return;
      var self = this;

      Object.keys(this.markers).forEach(function (id) { self.map.removeLayer(self.markers[id]); });
      this.markers = {};

      items.filter(function (a) { return a.located; }).forEach(function (a) {
        var marker = L.marker([a.lat, a.lng], { icon: self.logoIcon(a.status === 'online') }).addTo(self.map);
        marker.bindPopup(self.popupHtml(a), { minWidth: 220 });
        self.markers[a.id] = marker;
      });

      if (this.userMarker) { this.map.removeLayer(this.userMarker); this.userMarker = null; }
      if (this.userPos) {
        this.userMarker = L.marker(this.userPos, { icon: this.userIcon() })
          .addTo(this.map)
          .bindPopup('<strong>' + esc(this.labels.yourPosition) + '</strong>');
      }
    }

    popupHtml(a) {
      var hours = formatOpeningHours(a.openingHours, this.labels);
      var future = isFutureKickoff(a.kickoffDate);
      return '' +
        '<div class="dd-standorte__popup">' +
          '<h4>' + esc(a.name) + '</h4>' +
          '<p class="dd-standorte__address">' + esc(a.address) + '</p>' +
          (future ? '<p><span class="dd-standorte__badge">' + esc(this.labels.comingSoon) + '</span> ' + esc(formatKickoff(a.kickoffDate, this.labels.maintenance)) + '</p>' : '') +
          '<div class="dd-standorte__hours">' + hours.map(function (row) {
            return '<div class="dd-standorte__hours-row' + (row.isToday ? ' is-today' : '') + '">' +
              (row.label ? '<span class="dd-standorte__hours-day">' + esc(row.label) + '</span>' : '') +
              '<span>' + esc(row.value) + '</span></div>';
          }).join('') + '</div>' +
          (a.distance !== null ? '<p class="dd-standorte__distance">' + a.distance.toFixed(1) + ' ' + esc(this.labels.away) + '</p>' : '') +
          (a.products ? '<p class="dd-standorte__products">' + esc(a.products) + '</p>' : '') +
          '<a class="dd-standorte__route" href="https://www.google.com/maps/dir/?api=1&amp;destination=' + a.lat + ',' + a.lng + '" target="_blank" rel="noopener noreferrer">' + esc(this.labels.route) + '</a>' +
        '</div>';
    }

    fitMap(items) {
      if (!this.map) return;
      var points = items.filter(function (a) { return a.located; }).map(function (a) { return [a.lat, a.lng]; });
      if (this.userPos) points.push(this.userPos);

      if (points.length === 0) {
        this.map.setView(GERMANY, 5);
      } else if (points.length === 1) {
        this.map.setView(points[0], 13);
      } else {
        this.map.fitBounds(points, { padding: [40, 40], maxZoom: 12 });
      }
      var self = this;
      setTimeout(function () { self.map.invalidateSize(); }, 60);
    }

    focusMarker(id) {
      var marker = this.markers[id];
      if (!marker || !this.map) return;
      this.map.setView(marker.getLatLng(), 15, { animate: true });
      marker.openPopup();
      this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    search() {
      var self = this;
      var q = (this.input && this.input.value || '').trim();
      if (!q) return;

      this.setStatus(this.labels.loading, 'info');
      fetch('https://nominatim.openstreetmap.org/search?format=json&countrycodes=de&limit=1&q=' + encodeURIComponent(q))
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!Array.isArray(data) || data.length === 0) {
            self.setStatus(self.labels.notFound, 'error');
            return;
          }
          self.userPos = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          self.render();

          var nearest = self.withDistance()
            .filter(function (a) { return a.distance !== null; })
            .sort(function (a, b) { return a.distance - b.distance; })[0];
          if (nearest) setTimeout(function () { self.focusMarker(nearest.id); }, 300);
        })
        .catch(function () { self.setStatus(self.labels.notFound, 'error'); });
    }

    geolocate() {
      var self = this;
      if (!navigator.geolocation) {
        this.setStatus(this.labels.geoUnsupported, 'error');
        return;
      }
      this.setStatus(this.labels.loading, 'info');
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          self.userPos = [pos.coords.latitude, pos.coords.longitude];
          self.render();
        },
        function () { self.setStatus(self.labels.geoFailed, 'error'); },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }

    reset() {
      this.userPos = null;
      this.usingFallback = false;
      if (this.input) this.input.value = '';
      var clearBtn = this.querySelector('[data-dd-clear]');
      if (clearBtn) clearBtn.hidden = true;
      this.setStatus('', 'info');
      this.render();
    }

    openLightbox(src, alt) {
      var box = this.querySelector('[data-dd-lightbox]');
      if (!box || !src) return;
      var img = box.querySelector('img');
      img.src = src;
      img.alt = alt || '';
      box.hidden = false;
      document.documentElement.classList.add('dd-standorte-lightbox-open');
    }

    /* Dati strutturati emessi dopo il fetch, come faceva la pagina React
       (buildVendingMachineJsonLd in dd-repo/src/lib/seo/jsonld.ts). Solo gli
       automaten con indirizzo vero: pubblicare uno Store con indirizzo vuoto
       significa dare a Google un punto vendita inesistente. */
    injectJsonLd() {
      var located = this.automaten.filter(function (a) { return a.located; });
      if (located.length === 0) return;

      var origin = window.location.origin;
      var pageUrl = origin + window.location.pathname;
      var self = this;

      var graph = located.map(function (a) {
        var ld = {
          '@context': 'https://schema.org',
          '@type': 'Store',
          additionalType: 'https://schema.org/VendingMachine',
          '@id': pageUrl + '#' + a.id,
          name: a.name,
          url: pageUrl,
          address: { '@type': 'PostalAddress', streetAddress: a.address, addressCountry: 'DE' },
          geo: { '@type': 'GeoCoordinates', latitude: a.lat, longitude: a.lng },
          parentOrganization: { '@id': origin + '/#organization' }
        };
        if (a.products) ld.description = a.products;
        var spec = self.openingHoursSpec(a.openingHours);
        if (spec.length > 0) ld.openingHoursSpecification = spec;
        return ld;
      });

      var existing = this.querySelector('script[data-dd-jsonld]');
      if (existing) existing.remove();

      var script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dd-jsonld', '');
      script.textContent = JSON.stringify(graph);
      this.appendChild(script);
    }

    openingHoursSpec(hours) {
      if (!Array.isArray(hours) || is24h(hours)) return [];
      var out = [];
      hours.forEach(function (d) {
        if (!d || d.closed || !Array.isArray(d.slots)) return;
        var dow = SCHEMA_DAY[d.day];
        if (!dow) return;
        d.slots.forEach(function (slot) {
          out.push({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [dow],
            opens: slot.open,
            closes: slot.close === '24:00' ? '23:59' : slot.close
          });
        });
      });
      return out;
    }

    closeLightbox() {
      var box = this.querySelector('[data-dd-lightbox]');
      if (!box || box.hidden) return;
      box.hidden = true;
      document.documentElement.classList.remove('dd-standorte-lightbox-open');
    }
  }

  if (!customElements.get('dd-standorte')) {
    customElements.define('dd-standorte', DDStandorteElement);
  }
})();
